import Modal from "@/Components/Modal";
import {useDropzone} from 'react-dropzone';
import React, {CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import {bytesToSize, formatMinutes, getLanguageName, replaceFileExtension} from "@/utils/textFormatting";
import uploadFile from "@/services/localFileUploader"
import {v4 as uuidv4} from "uuid";
import {faEye, faLink, faMicrophone, faVolumeHigh} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {AudioExtractionParams, ExtractorProps, Folder, JobDetails, MediaMetaData, OcrExtractionParams} from "@/types";
import Spinner from "@/Components/Spinner";
import {getMediaMetadata} from "@/services/extractionJobManager";
import Popover from "@/Components/Popover";
import RemoteUploadModal from "@/Pages/Dashboard/Partials/RemoteUploadModal";
import {ExtractionMethod} from "@/types/enums";
import AdvancedSettingsModal, {
    DEFAULT_SETTINGS,
    DEFAULT_SUBTITLE_POSITION
} from "@/Pages/Dashboard/Partials/AdvancedSettingsModal";
import CloseIcon from "@/Components/Icons/CloseIcon";
import AudioExtractor from "@/services/media/AudioExtractor";
import PurchaseModal from "@/Pages/Dashboard/Purchase/PurchaseModal";
import UploadItem from "@/Pages/Dashboard/Upload/UploadItem";
import FFmpegService from "@/services/media/FFmpegService";
import {isAudioFile} from "@/utils/fileUtils";
import useSupportedLanguages, {DEFAULT_LANGUAGE} from "@/hooks/useSupportedLanguages";
import InsufficientCreditsException from "@/exceptions/InsufficientCreditsException";
import {useConfirm} from "@/Components/ConfirmDialog";
import BatchJobSubmissionException from "@/exceptions/BatchJobSubmissionException";
import TranslateIcon from "@/Components/Icons/TranslateIcon";
import WorkflowSelect from "@/Pages/Dashboard/Partials/WorkflowSelect";
import WorkflowFormModal from "@/Pages/Workflow/Components/WorkflowFormModal";
import {getWorkflows} from "@/services/workflowService";
import {Workflow} from "@/types/workflow";
import Select from "@/Components/Select";
import useComputeReservation from "@/hooks/useComputeReservation";
import {useDarkMode} from "@/hooks/useDarkMode";
import mediaFileCacheService from "@/services/MediaFileCacheService";

const ETA_UPDATE_INTERVAL = 1000
const MAX_AUDIO_EXTRACTORS = 2

export type UploadModalProps = {
    credits: number,
    currentFolder?: Folder | null,
    onCreditRefresh: () => Promise<number>,
    show: boolean,
    onClose: () => void,
    submit: (uploads: JobDetails[]) => Promise<void>,
    extractorParams: ExtractorProps,
    initialFiles?: File[],
    initialUpload?: RemoteUpload | null,
    ignoreSoftcodedSubtitles: boolean,
    autoSubmit: boolean,
    isPaidUser: boolean
}

export interface Upload {
    id: string,
    fileName: string,
    fileSize: number,
    progress: number,
    isAudioOnly?: boolean,
    options: Record<string, any>,
    error?: string,
    abortController?: AbortController,
}

export interface LocalUpload extends Upload {
    file: File,
    abortController: AbortController,
    uploadDuration?: number
    originalFile?: File
    step?: 'uploading' | 'extracting',
}

export interface RemoteUpload extends Upload {
    url: string
}

export type ProgressEta = {
    etaSeconds: number,
    lastUpdate: number,
    step?: 'uploading' | 'extracting'
}

function getUploadSettingsKey(folderId?: string | number | null) {
    return folderId ? `folder_${folderId}_upload_settings` : 'upload_settings';
}

export default function ({
    show,
    onClose,
    extractorParams,
    submit,
    initialFiles,
    isPaidUser,
    credits: initialCredits,
    currentFolder,
    onCreditRefresh,
    initialUpload,
    ignoreSoftcodedSubtitles,
    autoSubmit
}: UploadModalProps) {
    const audioExtractorPool = useRef<AudioExtractor[]>([]);
    const hasSubmittedRef = useRef(false)
    const [credits, setCredits] = useState(initialCredits)
    const triggerFileDrop = useRef<Function>()
    const [language, setLanguage] = useState(DEFAULT_LANGUAGE)
    const [uploads, setUploads] = useState<(LocalUpload | RemoteUpload)[]>([])
    const [method, setMethod] = useState<ExtractionMethod>(ExtractionMethod.OCR)
    const [autoExtract, setAutoExtract] = useState(autoSubmit);
    const [mediaMetadata, setMediaMetadata] = useState<Record<string, MediaMetaData>>({})
    const [uploadDurations, setUploadDurations] = useState<Record<string, number>>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showRemoteUploadModal, setShowRemoteUploadModal] = useState(false)
    const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false)
    const [showWorkflowModal, setShowWorkflowModal] = useState(false)
    const [advancedSettingsId, setAdvancedSettingsId] = useState<string | null>(null)
    const [submissionError, setSubmissionError] = useState<string>('')
    const [uploadEta, setUploadEta] = useState<Record<string, ProgressEta>>({})
    const [disableAudioExtractions, setDisableAudioExtractions] = useState<Set<string>>(new Set())
    const [languageOptions, isLoadingLang] = useSupportedLanguages('extraction', method);
    const [workflows, setWorkflows] = useState<Workflow[]>([])
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(null)
    const canUploadMultiple = isPaidUser;
    const confirm = useConfirm()
    const darkMode = useDarkMode()

    const {acceptedFiles, fileRejections, getRootProps, getInputProps} = useDropzone({
        multiple: canUploadMultiple,
        accept: {
            'video/*': [],
            'audio/*': [],
            'video/mp2t': ['.mts', '.m2ts'],
            'video/x-matroska': ['.mkv'],
            'text/plain': ['.sup'],
            'application/vnd.ms-asf': ['.asf']
        },
        maxSize: extractorParams.maxFileSize
    });

    const totalUploadEta = useMemo(() =>
        Object.values(uploadEta).reduce(
            (total, eta) => total + eta.etaSeconds + (eta.step === 'extracting' ? 60 : 0), // todo get better estimate of upload speed
            0
        ),
        [uploadEta]
    )

    const reservationEnabled = method === ExtractionMethod.AUDIO && autoExtract && isPaidUser;
    const {reservedInstances, isReservationActive, cancelReservation, commitReservations, getReservedInstance} = useComputeReservation({
        uploads,
        uploadDurations,
        totalUploadEta,
        enabled: reservationEnabled,
        userCredits: credits,
    })

    function getUploadEffectiveDuration(upload: Upload) {
        if (upload.options.timeEnd) {
            const start = upload.options.timeStart || 0
            const end = upload.options.timeEnd
            const duration = end - start
            if (duration > 0) {
                return duration
            }
        }
        if (!mediaMetadata[upload.id]) {
            return undefined
        }
        return method === ExtractionMethod.OCR ?
            mediaMetadata[upload.id].videos[0]?.duration :
            mediaMetadata[upload.id].audio[0]?.duration
    }

    useEffect(() => {
        const newDurations = uploads.reduce((acc, upload) =>
                ({...acc, [upload.id]: getUploadEffectiveDuration(upload)})
            , {})
        setUploadDurations(newDurations)
    }, [uploads, mediaMetadata, method])

    const creditsNeeded = useMemo(() =>
            Object.values(uploadDurations).reduce((total, duration) => total + Math.ceil((duration || 0) / 60), 0),
        [uploadDurations]
    )

    const isUploadsComplete = useMemo(() => uploads.every(upload =>
        upload.progress === 1 && ('url' in upload || upload.step !== 'extracting')
    ) && uploads.length > 0, [uploads]);

    const updateUpload = (id: string, update: Partial<LocalUpload | RemoteUpload>) => {
        setUploads(prev =>
            prev.map(upload => upload.id === id ? {...upload, ...update} : upload))
    }

    useEffect(() => {
        setCredits(initialCredits)
    }, [initialCredits]);

    useEffect(() => {
        FFmpegService.preload().catch(e => console.error(e))
    }, []);

    useEffect(() => {
        setAutoExtract(autoSubmit);
    }, [show]);

    useEffect(() => {
        if (autoExtract && isUploadsComplete) {
            setTimeout(() => setIsSubmitting(true), 1000)
        }
    }, [autoExtract]);

    useEffect(() => {
        if (!show) {
            setUploads([])
            setIsSubmitting(false)
            hasSubmittedRef.current = false
            setSubmissionError('')
            setShowRemoteUploadModal(false)
        }
    }, [show]);

    useEffect(() => {
        if (getRootProps().onDrop) {
            triggerFileDrop.current = getRootProps().onDrop
        }
    }, [getRootProps]);

    useEffect(() => {
        try {
            const key = getUploadSettingsKey(currentFolder?.id);
            const stored = localStorage.getItem(key);
            if (stored) {
                const {
                    language: storedLanguage = DEFAULT_LANGUAGE,
                    method: storedMethod = ExtractionMethod.OCR
                } = JSON.parse(stored);
                setLanguage(storedLanguage);
                setMethod(
                    storedMethod === ExtractionMethod.AUDIO ? ExtractionMethod.AUDIO : ExtractionMethod.OCR
                );
            }
        } catch (e) {
            console.error('Failed to parse upload settings', e);
        }
    }, [currentFolder?.id]);

    useEffect(() => {
        const key = getUploadSettingsKey(currentFolder?.id);
        localStorage.setItem(key, JSON.stringify({ language, method }));
    }, [language, method, currentFolder?.id]);

    useEffect(() => {
        if (!languageOptions.map(opt => opt.value).includes(language) && !isLoadingLang) {
            setLanguage(DEFAULT_LANGUAGE)
        }
    }, [method, languageOptions]);

    useEffect(() => {
        getWorkflows()
            .then(res => {
                const defaultWorkflow = res.find(el => el.isDefault);
                setWorkflows(res);
                if (defaultWorkflow) {
                    setSelectedWorkflowId(defaultWorkflow.id);
                }
            })
            .catch(err => toast.error('Failed to load workflows'))
    }, []);

    useEffect(() => {
        const handlePageHide = () => {
            if (uploads.length > 0 && !isSubmitting) {
                cancelReservation(null, "page_hide");
            }
        };

        window.addEventListener('pagehide', handlePageHide);
        return () => window.removeEventListener('pagehide', handlePageHide);
    }, [uploads.length, isSubmitting]);

    useEffect(() => {
        uploads.forEach(upload => {
            if ('file' in upload) {
                if (method === ExtractionMethod.OCR) {
                    if (upload.isAudioOnly && upload.originalFile) {
                        transitionAudioUploadToVideo(upload)
                    }
                } else if (canExtractAudio(upload) && upload.progress < 0.9) {
                    transitionVideoUploadToAudio(upload)
                }
            }
        })
    }, [method, uploads]);

    useEffect(() => {
        if(method === ExtractionMethod.OCR && Object.keys(reservedInstances).length) {
            const timeout = setTimeout(
                () => cancelReservation(Object.keys(reservedInstances), "method_changed"),
                2_500 // handle rapid method changes
            )
            return () => clearTimeout(timeout)
        }
    }, [method, reservedInstances]);

    useEffect(() => {
        if (initialFiles?.length) {
            try {
                const dataTransfer = new DataTransfer();
                initialFiles.forEach(file => {
                    const cleanFile = new File([file], file.name, { type: file.type, lastModified: file.lastModified });
                    dataTransfer.items.add(cleanFile);
                })
                const event = new DragEvent("drop", {
                    bubbles: true,
                    cancelable: true,
                    dataTransfer,
                });
                // @ts-ignore
                event.persist = () => {
                };
                // @ts-ignore
                triggerFileDrop.current(event)
            } catch (e) {
                console.error("Browser does not support DataTransfer API", e)
            }
        }
    }, [initialFiles]);

    useEffect(() => {
        if (initialUpload) {
            setUploads(prev => prev.concat(initialUpload))
            loadMetadata(initialUpload.id)
        }
    }, [initialUpload]);

    useEffect(() => {
        const newFiles = acceptedFiles
            .filter(file => !uploads.some(upload => "file" in upload && isFileSame(upload.file, file)))
            .slice(0, canUploadMultiple ? acceptedFiles.length : 1)
            .map((file): LocalUpload => ({
                file,
                fileName: file.name,
                fileSize: file.size,
                progress: 0,
                id: uuidv4(),
                options: {},
                isAudioOnly: isAudioFile(file),
                abortController: new AbortController()
            }))

        if (newFiles.length && !canUploadMultiple && uploads.length > 0) {
            showMultipleUploadError();
        } else {
            if (newFiles.some(item => item.isAudioOnly)) {
                setMethod(ExtractionMethod.AUDIO)
            }
            newFiles.forEach(upload => {
                mediaFileCacheService.saveFile(upload.id, upload.file)
                beginUpload(upload)
            })
            setUploads(prev => prev.concat(newFiles))
        }
    }, [acceptedFiles]);

    function isFileSame(file1: File, file2: File) {
        return file1.name === file2.name && file1.size === file2.size
    }

    function showMultipleUploadError() {
        toast.error('Trial version only allows one file at a time. To upload multiple files, please purchase credits.')
    }

    useEffect(() => {
        if (fileRejections.length > 0) {
            const code = fileRejections[0].errors[0].code;
            if (code === 'file-too-large') {
                toast.error(`File is too large. Max file size is ${bytesToSize(extractorParams.maxFileSize)}`);
            } else if (code === 'file-invalid-type') {
                toast.error('Unsupported file type. Please select a video or audio file.');
            }
        }
    }, [fileRejections]);

    useEffect(() => {
        if (isUploadsComplete && autoExtract) {
            setIsSubmitting(true)
        }
    }, [isUploadsComplete]);

    useEffect(() => {
        if (
            isSubmitting &&
            !hasSubmittedRef.current &&
            uploads.length > 0 &&
            !isReservationActive &&
            uploads.every(upload => mediaMetadata[upload.id] !== undefined)
        ) {
            const items: JobDetails[] = uploads.map(upload => (
                {
                    fileName: upload.fileName,
                    fileSize: upload.fileSize,
                    folderId: currentFolder?.id || null,
                    id: upload.id,
                    workflowId: selectedWorkflowId,
                    language,
                    method: method as ExtractionMethod.OCR | ExtractionMethod.AUDIO,
                    extractionParams: getExtractionParams(upload),
                    metaData: {
                        source: ('url' in upload && upload.url) ? 'remote' : 'local',
                        ...'file' in upload ? {uploadDuration: upload.uploadDuration} : {},
                        ...'url' in upload && upload.url ? {host: (new URL(upload.url).host)} : {},
                        ...autoExtract ? {autoSubmit: true} : {},
                        ...getReservedInstance(upload.id) ? {warmupApplied: getReservedInstance(upload.id)} : {},
                        ...upload.isAudioOnly && 'originalFile' in upload ? {
                            'extractedAudio': true,
                            originalFileSize: 'originalFile' in upload && upload.originalFile?.size
                        } : {},
                    }
                }
            ));

            if (
                method === ExtractionMethod.OCR &&
                uploads.some(u => u.isAudioOnly)
            ) {
                setIsSubmitting(false)
                toast.error('Audio file(s) detected in Vision mode. ' +
                    'Please remove any audio files or switch to Audio extraction mode to process your upload.')
            } else {
                hasSubmittedRef.current = true
                commitReservations()
                submitUploads(items)
            }
        }
    }, [isSubmitting, mediaMetadata, isReservationActive]);

    function getExtractionParams(upload: Upload): OcrExtractionParams | AudioExtractionParams {
        const metadata = mediaMetadata[upload.id];

        if (Object.keys(upload.options).length === 0 && method === ExtractionMethod.OCR) {
            if (metadata.subtitles.length > 0) {
                return {ignoreSoftCodedSubtitles: upload.fileName.endsWith('.sup') ? false : ignoreSoftcodedSubtitles}
            } else {
                return {}
            }
        }

        const params = {
            ...(upload.options.timeEnd !== undefined ? {timeStart: upload.options.timeStart || 0} : {}),
            ...(upload.options.timeEnd !== undefined ? {timeEnd: upload.options.timeEnd} : {})
        }

        if (method === ExtractionMethod.AUDIO) {
            return params;
        } else {
            return {
                ...params,
                subtitlePosition: upload.options.position || DEFAULT_SUBTITLE_POSITION,
                ...(upload.options.yOffsetRatio ? {yOffsetRatio: upload.options.yOffsetRatio} : {}),
                ...(upload.options.xOffsetRatio ? {xOffsetRatio: upload.options.xOffsetRatio} : {}),
                ...(upload.options.softCodedSubtitles === false && metadata.subtitles.length ? {ignoreSoftCodedSubtitles: true} : {}),
                ...(upload.options.filterNoiseText === false ? {filterNoiseText: false} : {}),
            }
        }
    }

    function submitUploads(items: JobDetails[]) {
        submit(items).catch(error => {
            let message = ''

            if (error instanceof InsufficientCreditsException) {
                setCredits(error.currentBalance)
                setShowCreditPurchaseModal(true)
            } else if (error instanceof BatchJobSubmissionException) {
                setUploads(prev =>
                    prev.map(upload => ({...upload, error: error.jobErrors[upload.id]}))
                )
                if (error.batchErrors.length) {
                    message = error.batchErrors[0];
                }
            } else {
                message = error.message || 'Failed to submit uploads.';
            }

            hasSubmittedRef.current = false
            setIsSubmitting(false);
            setSubmissionError(message)
        })
    }

    function clearMetadata(id: string) {
        setMediaMetadata(({[id]: _, ...rest}) => rest)
    }

    function transitionAudioUploadToVideo(upload: LocalUpload) {
        upload.abortController.abort()
        upload.abortController = new AbortController()
        upload.file = upload.originalFile!
        upload.fileName = upload.originalFile!.name
        clearMetadata(upload.id)
        updateUpload(upload.id, {...upload, isAudioOnly: false, step: 'uploading', progress: 0})
        startUpload(upload)
    }

    function transitionVideoUploadToAudio(upload: LocalUpload) {
        const extractor = getAvailableAudioExtractor();
        if (!extractor) {
            return;
        }
        upload.abortController.abort()
        upload.originalFile = upload.file
        upload.abortController = new AbortController()
        clearMetadata(upload.id)
        const update: LocalUpload = {...upload, isAudioOnly: true, step: 'extracting', progress: 0};
        updateUpload(upload.id, update)
        setUploadEta(prev => {
            delete prev[upload.id]
            return prev
        })
        extractAudioAndUpload(update, extractor)
    }

    function handleOptionsChange(options: Record<string, any>) {
        options = Object.fromEntries(Object.entries(options).filter(([_, value]) => value !== undefined))
        setUploads(prev =>
            prev.map(upload => upload.id === advancedSettingsId ? {
                ...upload,
                options
            } : upload)
        )
    }

    async function loadMetadata(uploadId: string) {
        try {
            const data = await getMediaMetadata(uploadId)
            setMediaMetadata(prev => ({...prev, [uploadId]: data}))
            updateUpload(uploadId, {error: ''})
        } catch (err: any) {
            updateUpload(uploadId, {error: err.message})
        }
    }

    function canExtractAudio(upload: LocalUpload): boolean {
        return method === ExtractionMethod.AUDIO &&
            !upload.isAudioOnly &&
            !disableAudioExtractions.has(upload.id)
    }

    function getAvailableAudioExtractor(): AudioExtractor | null {
        const availableExtractor = audioExtractorPool.current.find(extractor => !extractor.running());
        if (availableExtractor) {
            return availableExtractor;
        }

        if (audioExtractorPool.current.length < MAX_AUDIO_EXTRACTORS) {
            const newExtractor = new AudioExtractor();
            audioExtractorPool.current.push(newExtractor);
            return newExtractor;
        }

        return null;
    }

    async function extractAudioAndUpload(upload: LocalUpload, extractor: AudioExtractor) {
        try {
            const signal = upload.abortController.signal
            const start = Date.now()
            const {data: audioData, extension} = await extractor.extractAudio(
                upload.file,
                signal,
                (progress, secondsLeft) => {
                    if (!signal.aborted) {
                        updateUpload(upload.id, {progress})
                        const eta: ProgressEta = {etaSeconds: secondsLeft, lastUpdate: Date.now(), step: 'extracting'};
                        setUploadEta(prev => ({
                            ...prev,
                            [upload.id]: eta
                        }))
                    }
                });
            const end = Date.now();
            const audioFile = new File([audioData], replaceFileExtension(upload.file.name, extension))
            const audioUpload: LocalUpload = {
                ...upload,
                uploadDuration: Math.round((end - start) / 1000),
                file: audioFile,
                fileName: audioFile.name,
                originalFile: upload.file,
                step: 'uploading'
            }
            setUploadEta(({[upload.id]: _, ...rest}) => rest)
            updateUpload(upload.id, audioUpload)
            await startUpload(audioUpload)
        } catch (err: any) {
            if (err.name !== 'AbortError') {
                setDisableAudioExtractions(prev => prev.add(upload.id))
                transitionAudioUploadToVideo(upload)
            }
        }
    }

    function beginUpload(upload: LocalUpload) {
        const extractor = canExtractAudio(upload) ? getAvailableAudioExtractor() : null
        if (!extractor) {
            return startUpload(upload)
        }
        upload.originalFile = upload.file
        upload.isAudioOnly = true
        upload.step = 'extracting'
        void extractAudioAndUpload(upload, extractor)
    }

    async function startUpload(upload: LocalUpload) {
        try {
            const signal = upload.abortController.signal
            const start = Date.now()
            await uploadFile({
                file: upload.file,
                name: upload.id,
                partSize: extractorParams.maxFilePartSize,
                maxConcurrentParts: extractorParams.maxConcurrentPartUploads,
                signal: upload.abortController.signal,
                progressCallback: (percentage, secondsLeft) => {
                    if (signal.aborted) {
                        return;
                    }
                    if (secondsLeft !== undefined) {
                        const eta = {etaSeconds: secondsLeft, lastUpdate: Date.now()};
                        setUploadEta(prev => ({
                            ...prev,
                            [upload.id]: eta.lastUpdate - (prev[upload.id]?.lastUpdate || 0) > ETA_UPDATE_INTERVAL ? eta : prev[upload.id]
                        }))
                    }
                    updateUpload(upload.id, {progress: percentage})
                }
            });
            const duration = Math.round((Date.now() - start) / 1000)
            updateUpload(upload.id, {uploadDuration: (upload.uploadDuration || 0) + duration})
            loadMetadata(upload.id)
        } catch (err: any) {
            const message = err.message || err
            if (message.toString().includes('aborted the upload')) {
                return
            }
            toast.error(typeof message === 'string' ? message : JSON.stringify(message))
            setUploads(prev => prev.filter(up => up.id !== upload.id))
        }
    }

    function cancelUploads() {
        uploads.forEach(upload => upload.abortController?.abort())
        cancelReservation(null, "modal_closed")
        setUploads([])
        setUploadEta({})
    }

    function cancelUpload(upload: Upload) {
        upload.abortController?.abort()
        setUploads(prev => prev.filter(up => up.id !== upload.id))
        mediaFileCacheService.clearFile(upload.id)
        setUploadEta(({[upload.id]: _, ...rest}) => rest)
        if (getReservedInstance(upload.id)) {
            cancelReservation([upload.id], "upload_canceled")
        }
        setSubmissionError('')
    }

    async function handleClose() {
        if (!uploads.length || await confirm('Are you sure you want to cancel the uploads?')) {
            cancelUploads()
            onClose()
        }
    }

    const submitDisabled = !isUploadsComplete || isSubmitting
    const hasInsufficientCredits = creditsNeeded > credits
    const currSettingsUpload = uploads.find(up => up.id === advancedSettingsId)
    const advancedSettings = {
        ...DEFAULT_SETTINGS,
        softCodedSubtitles: currSettingsUpload?.fileName?.endsWith('.sup') ? true : !ignoreSoftcodedSubtitles,
        ...(currSettingsUpload?.options || {})
    }
    return (
        <Modal maxWidth='xl' show={show} onClose={handleClose}>
            <div id="default-modal" className="w-full">
                <RemoteUploadModal
                    show={showRemoteUploadModal}
                    onClose={() => setShowRemoteUploadModal(false)}
                    onUploadComplete={upload => {
                        setUploads(prev => prev.concat(upload))
                        setShowRemoteUploadModal(false)
                        loadMetadata(upload.id)
                        toast.success('Video uploaded successfully')
                    }}
                />

                <AdvancedSettingsModal
                    show={advancedSettingsId != null}
                    initSettings={advancedSettings}
                    videoFile={(uploads.find(up => up.id === advancedSettingsId) as LocalUpload)?.file}
                    metadata={advancedSettingsId ? mediaMetadata[advancedSettingsId] : undefined}
                    isAudioOnly={method === ExtractionMethod.AUDIO || uploads.find(up => up.id === advancedSettingsId)?.isAudioOnly}
                    onSave={({position, yOffsetRatio, xOffsetRatio, softCodedSubtitles, filterNoiseText, timeStart, timeEnd}) => {
                        handleOptionsChange({
                            position: position !== DEFAULT_SUBTITLE_POSITION ? position : undefined,
                            yOffsetRatio: position === 'custom' ? yOffsetRatio : undefined,
                            xOffsetRatio: position === 'custom' ? xOffsetRatio : undefined,
                            softCodedSubtitles,
                            filterNoiseText,
                            timeStart: timeStart || undefined,
                            timeEnd
                        })
                    }}
                    onClose={() => setAdvancedSettingsId(null)}
                />
                <PurchaseModal
                    show={showCreditPurchaseModal}
                    onCompleted={async () => {
                        setShowCreditPurchaseModal(false)
                        const currentCredits = await onCreditRefresh()
                        if (creditsNeeded <= currentCredits) {
                            setIsSubmitting(true)
                        }
                    }}
                    message={`You have ${formatMinutes(credits)} of processing credits remaining, but need ${formatMinutes(creditsNeeded)} to process the selected videos.`}
                    onClose={() => setShowCreditPurchaseModal(false)}
                    prices={extractorParams.pricing}
                />
                <WorkflowFormModal
                    isPaidUser={isPaidUser}
                    show={showWorkflowModal}
                    onClose={() => setShowWorkflowModal(false)}
                    onSave={(workflow) => {
                        setWorkflows(prev => prev.concat(workflow))
                        setShowWorkflowModal(false)
                        setSelectedWorkflowId(workflow.id)
                    }}
                    workflow={undefined}
                />
                <div className="flex items-center p-4 md:p-7">
                    <div className="w-8 h-8"></div>
                    <div className="flex-grow flex justify-center">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                            Extract Subtitles
                        </h3>
                    </div>
                    <button type="button"
                            onClick={handleClose}
                            className="text-gray-400 dark:text-gray-500 bg-transparent hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center !outline-none"
                    >
                        <CloseIcon/>
                        <span className="sr-only">Close modal</span>
                    </button>
                </div>
                <div className="px-10 pb-6">
                    <div {...getRootProps({className: 'dropzone flex items-center justify-center w-full'})} >
                        {/* No accept attribute: Safari greys out extensions it can't map, like .mkv. Dropzone still validates */}
                        <input {...getInputProps({accept: undefined})} />
                        <label htmlFor="dropzone-file"
                               className={`flex transition-all flex-col items-center justify-center w-full ${uploads.length > 0 ? 'h-50' : 'h-60'} rounded-lg border-2 border-gray-300 dark:border-gray-600 border-dashed  bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer`}>
                            <div className="flex flex-col items-center justify-center w-full  pt-5 pb-6">
                                <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                                     aria-hidden="true"
                                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                                </svg>
                                <p className="mb-1 md:mb-2 text-sm text-gray-500 dark:text-gray-400 text-center"><span
                                    className="font-semibold">Click to upload</span> <span>or drag and drop</span></p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mx-5 text-center">Video or audio files up
                                    to {bytesToSize(extractorParams.maxFileSize)} in size</p>
                                <Popover
                                    disabled={isPaidUser}
                                    delay={0}
                                    trigger="hover"
                                    placement="top"
                                    content={
                                        <div className="text-sm text-gray-500 dark:text-gray-400 p-2 text-center">
                                            <p>Remote uploads are not available in the free trial</p>
                                        </div>
                                    }
                                >
                                    <button
                                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                                            e.stopPropagation()
                                            isPaidUser && setShowRemoteUploadModal(true)
                                        }}
                                        className={`
                                        px-4 py-2 me-2  duration-150 text-sm  text-black dark:text-gray-200 focus:outline-none rounded-md border border-gray-300 dark:border-gray-600 mt-5 bg-none
                                        ${!isPaidUser ? 'cursor-not-allowed hover:opacity-50 !outline-none' : 'hover:bg-white dark:hover:bg-gray-700 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-600 focus:z-10 hover:text-blue-700 dark:hover:text-blue-400'}
                                    `}>
                                        <FontAwesomeIcon icon={faLink} size="sm" className="mr-2"/> <span>Upload from URL</span>
                                    </button>

                                </Popover>

                            </div>

                        </label>
                    </div>


                    <div
                        className={`flex flex-col mt-8 bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-sm rounded-md ${!uploads.length ? 'hidden' : ''}`}>

                        <div className="p-4 md:p-5 space-y-7">
                            {uploads.map((upload) => (
                                <UploadItem
                                    key={upload.id}
                                    upload={upload}
                                    showAdvancedSettings={() => setAdvancedSettingsId(upload.id)}
                                    cancelUpload={() => cancelUpload(upload)}
                                    uploadEta={uploadEta[upload.id]?.etaSeconds}
                                    duration={uploadDurations[upload.id]}
                                />))
                            }
                        </div>
                    </div>
                </div>

                <div className="px-10 pb-6 pt-0">
                    <div className="text-left  ">
                        <div className="flex w-full">
                            <Popover
                                delay={200}
                                trigger="hover"
                                placement="top"
                                aria-labelledby="default-popover"
                                content={
                                    <div className="w-64 text-sm text-gray-500 dark:text-gray-100">
                                        <div className="px-3 py-2">
                                            <p>Extracts visible, hardcoded subtitles using OCR. <b>Works only if
                                                subtitles are present on screen.</b></p>
                                        </div>
                                    </div>
                                }
                            >
                                <button type="button"
                                        onClick={() => setMethod(ExtractionMethod.OCR)}
                                        className={`px-4 grow py-3 text-sm font-medium flex flex-col items-center flex-1
                                       border-2 rounded  hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 ${method === ExtractionMethod.OCR ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'}`}>
                                    <FontAwesomeIcon icon={faEye} className="text-blue-700 dark:text-blue-400 mb-1.5" size="lg"/>
                                    <span className="font-bold text-md mb-0.5">Vision</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Extract hard-coded subtitles</span>
                                </button>
                            </Popover>


                            <div className="w-3"></div>

                            <Popover
                                delay={200}
                                trigger="hover"
                                placement="top"
                                aria-labelledby="default-popover"
                                content={
                                    <div className="w-64 text-sm text-gray-500 dark:text-gray-100">
                                        <div className="px-3 py-2">
                                            <p>Extracts subtitles from spoken dialogue. Best for videos without visible
                                                subtitles.</p>
                                        </div>
                                    </div>
                                }
                            >
                                <button type="button"
                                        onClick={() => setMethod(ExtractionMethod.AUDIO)}
                                        className={`px-4 grow py-3 text-sm font-medium flex flex-col items-center flex-1
                                        border-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 ${method === ExtractionMethod.AUDIO ? '  border-blue-600 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'}`}>
                                    <FontAwesomeIcon icon={faVolumeHigh} className="text-blue-700 dark:text-blue-400 mb-1.5" size="lg"/>
                                    <span className="font-bold text-md mb-0.5">Audio</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Extract subtitles from speech</span>
                                </button>
                            </Popover>
                        </div>

                    </div>
                </div>
                <div className="px-10 pb-4 pt-0">
                    <div className="text-left">
                        <label
                            className="text-sm font-medium mb-1.5 inline-block dark:text-gray-300">{method === ExtractionMethod.OCR ? 'Source language' : 'Speaker language'}</label>
                        <Select
                            variant="modal"
                            key={`${method}-${language}`}
                            menuPlacement="top"
                            inputId="lang-input"
                            onChange={(e) => setLanguage(e!.value)}
                            value={{label: getLanguageName(language), value: language}}
                            options={languageOptions}
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    borderRadius: '5px',
                                    'borderColor': darkMode ? '#526380' : '#dadada',
                                    'padding': '2px 0px',
                                })
                            }}
                            components={{
                                IndicatorSeparator: () => null,
                                SingleValue: (props) => (
                                    <div
                                        style={props.getStyles('singleValue', props) as CSSProperties} {...props.innerProps}>
                                        <span>
                                            {method === ExtractionMethod.OCR ?
                                                <TranslateIcon className="-mt-1 !mr-2"/> :
                                                <FontAwesomeIcon icon={faMicrophone} className="text-gray-400 mr-2"
                                                                 size="sm"/>
                                            }
                                        </span>
                                        <span>
                                            {props.children}
                                        </span>
                                    </div>
                                )
                            }}
                        />
                    </div>
                </div>


                <div className="px-10 pb-4">
                    <label className="text-sm font-medium mb-1.5 inline-block dark:text-gray-300">Workflow</label>
                    {useMemo(() => (
                        <WorkflowSelect
                            items={workflows}
                            onCreateNew={() => setShowWorkflowModal(true)}
                            selectedWorkflowId={selectedWorkflowId}
                            onSelect={(id) => setSelectedWorkflowId(id)}
                        />
                    ), [workflows, selectedWorkflowId])}
                </div>
                <div className="px-10 pb-4">
                    <div className="">
                        <label className="inline-flex items-center">
                            <input
                                name="autoExtract"
                                onChange={(e) => setAutoExtract(e.target.checked)}
                                type="checkbox"
                                checked={autoExtract}
                                className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded mr-2 focus:ring-transparent"/>
                            <span className={`text-sm mr-2 ${autoExtract ? 'text-black dark:text-gray-200' : 'text-gray-800 dark:text-gray-400'}`}>Start extraction automatically after uploads finish</span>
                        </label>
                    </div>
                </div>

                <div className="px-10 pb-8 mt-2">
                    {submissionError !== '' && (
                        <div
                            className="p-4 mb-4 text-sm text-red-800 dark:text-red-400 rounded-lg bg-red-50 dark:bg-red-900/30 -mt-4"
                            role="alert">
                            <span className="font-medium">Error!</span> {submissionError}
                        </div>
                    )}
                    <button onClick={() => setIsSubmitting(true)}
                            disabled={submitDisabled}
                            className={`transition-all w-full py-3 rounded-lg  ${submitDisabled ? 'bg-blue-300 dark:bg-blue-800 text-white' : 'text-white bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-800'}`}>
                        <span className="flex justify-center items-center">
                            <span>{isSubmitting ? 'Submitting' : 'Extract Subtitles'}</span>
                            {(isSubmitting) && <Spinner size={4} className="ml-2 fill-white"/>}
                        </span>
                    </button>
                </div>
            </div>
        </Modal>
    )
}
